/**
 * 경량 툴팁/도움말 팝오버 (Bootstrap 의존 없음, 사이트 공용)
 *
 * 선언형 — 아무 요소에 속성만 달면 클릭 시 팝오버가 뜬다(이벤트 위임이라 동적 삽입 OK):
 *   <i class="fas fa-question-circle" data-tip="짧은 안내문구"></i>
 *   <i class="fas fa-question-circle" data-tip-html="#feeHelpTpl" data-tip-title="수수료 안내"></i>
 *      └ data-tip      : 텍스트(이스케이프됨)
 *        data-tip-html : '#id' 면 해당 요소의 innerHTML, 아니면 raw HTML 문자열
 *        data-tip-title: (선택) 헤더 제목
 *        data-tip-place: (선택) 'bottom'(기본) | 'top'
 *        data-tip-hover: (선택) 값 없이 달면 **마우스오버로도** 뜬다.
 *                        라벨 없는 아이콘 버튼처럼 눌러보기 전에 뭔지 알아야 하는 곳에만.
 *
 * 프로그래매틱 —
 *   $j.Ui.Tooltip.show(anchorEl, '<b>내용</b>', { title:'제목', place:'bottom' });
 *   $j.Ui.Tooltip.show(anchorEl, html, { tone:'panel', maxWidth: 560 });   // 넓은 판
 *   $j.Ui.Tooltip.hide();
 *
 * 톤 — 껍데기의 성격을 고른다. `show(el, html, { tone:'panel' })`
 *
 *   memo  (기본)  크림+앰버 메모지. **읽는 것**이 든 안내문 — 도움말·알고리즘 설명
 *   panel         표·카드와 같은 흰 판. **누를 것**이 든 판 — 회원 액션·처리 메뉴
 *
 * 톤을 늘리려면 `TONES` 에 이름을 넣고 `.jb-tip--<이름>` CSS 한 벌을 더하면 된다.
 * show() 는 손대지 않는다 — 이름만 보고 클래스를 붙이고, 이전 톤은 알아서 걷어낸다.
 */
export default class Tooltip {
    /**
     * 쓸 수 있는 톤. 기본('memo')은 클래스를 붙이지 않는다 — 껍데기 기본 모양이 그것이다.
     * 여기에 없는 이름을 주면 기본으로 뜬다 (오타가 조용히 이상한 클래스를 붙이지 않도록).
     */
    static TONES = ['memo', 'panel'];

    constructor() {
        this.$panel = null;
        this.anchor = null;            // 현재 열려 있는 기준 요소(DOM)
        this._styleInjected = false;
        this._bindDelegation();
    }

    /* ───────────────── public ───────────────── */

    /** 기준 요소 옆에 HTML 내용으로 팝오버를 연다. 같은 요소를 다시 누르면 닫힌다(토글). */
    show = (anchor, html, opts = {}) => {
        const el = anchor instanceof $ ? anchor[0] : anchor;
        if (!el) return;
        if (this.anchor === el) { this.hide(); return; } // 토글

        const $p = this._getPanel();
        const title = opts.title
            ? `<div class="jb-tip-title">${this._esc(opts.title)}</div>` : '';
        $p.find('.jb-tip-body').html(title + html);

        this.anchor = el;
        // hover 로 뜬 것은 **마우스를 막지 않는다**(`is-hover` → pointer-events:none).
        // 좁은 패널(관리자 프로필 화면설정)에서는 팝오버가 바로 아래 버튼을 덮는데,
        // 그 위를 지나면 다음 버튼의 mouseenter 가 아예 안 나고 클릭도 팝오버가 먹는다.
        // 클릭으로 연 도움말은 그대로 둔다 — 안의 글자를 끌어 복사하는 자리다.
        $p.toggleClass('is-hover', !!opts.hover).addClass('is-open');
        this._applyTone($p, opts.tone);

        // 기본 폭(360px)은 안내문 기준이다. 판톤에 표까지 얹는 화면은 넓혀 쓴다.
        // 값을 안 주면 스타일시트 값으로 되돌린다 — 앞서 넓힌 것이 다음 툴팁에 남지 않게
        $p.css('max-width', opts.maxWidth
            ? (typeof opts.maxWidth === 'number' ? opts.maxWidth + 'px' : opts.maxWidth)
            : '');
        this._place(el, opts.place || 'bottom');
    };

    /**
     * 톤 클래스를 갈아끼운다.
     *
     * 패널을 하나만 만들어 돌려쓰므로(`_getPanel`), 이전에 열렸던 톤을 먼저 걷어내야 한다.
     * 안 그러면 판톤으로 한 번 연 뒤의 도움말이 계속 판톤으로 뜬다.
     */
    _applyTone = ($p, tone) => {
        Tooltip.TONES.forEach((t) => $p.removeClass('jb-tip--' + t));

        // 기본톤은 껍데기 그대로다 — 붙일 클래스가 없다
        if (tone && tone !== 'memo' && Tooltip.TONES.includes(tone)) {
            $p.addClass('jb-tip--' + tone);
        }
    };

    /** 텍스트 내용으로 연다(이스케이프). */
    text = (anchor, text, opts = {}) => this.show(anchor, this._esc(text), opts);

    /** 팝오버를 닫는다. */
    hide = () => {
        if (!this.$panel) return;
        this.$panel.removeClass('is-open');
        this.anchor = null;
    };

    /* ───────────────── internal ───────────────── */

    _bindDelegation() {
        // 선언형 트리거(이벤트 위임 — 동적으로 추가된 요소도 동작)
        $(document)
            .off('.jbtip')
            .on('click.jbtip', '[data-tip],[data-tip-html]', (e) => {
                const el = e.currentTarget;
                // hover 전용(`data-tip-hover`)은 클릭으로 열지 않는다 — 대신 **닫는다.**
                // 누르면 뭔가 일어나는 버튼들이라, 클릭까지 토글하면 방금 연 모달 위에 설명이
                // 남아 화면을 가린다. 그렇다고 그냥 두면 마우스가 버튼 위에 머무는 동안(누르고
                // 나서도) 팝오버가 모달 옆에 남는다. 눌렀다 = 읽기를 마쳤다이므로 접는다.
                if (el.hasAttribute('data-tip-hover')) { this.hide(); return; }
                e.preventDefault();
                e.stopPropagation();
                this.show(el, this._contentOf(el), {
                    title: el.getAttribute('data-tip-title') || '',
                    place: el.getAttribute('data-tip-place') || 'bottom',
                });
            })
            // 마우스오버로도 뜨게 하려면 `data-tip-hover` 를 함께 단다 (옵트인).
            // 기본을 hover 로 두지 않는 이유: 이 툴팁은 클릭해서 읽는 도움말로 쓰여왔고,
            // 전부 hover 로 바꾸면 마우스가 지나가기만 해도 팝오버가 튀어나온다.
            // 라벨 없는 아이콘 버튼처럼 "눌러보기 전에 뭔지 알아야 하는" 곳에만 붙인다.
            .on('mouseenter.jbtip', '[data-tip-hover]', (e) => {
                const el = e.currentTarget;
                clearTimeout(this._hoverT);
                if (this.anchor === el) return;
                if (this.anchor) this.hide();          // 다른 게 열려 있으면 교체
                this.show(el, this._contentOf(el), {
                    title: el.getAttribute('data-tip-title') || '',
                    place: el.getAttribute('data-tip-place') || 'bottom',
                    hover: true,
                });
            })
            .on('mouseleave.jbtip', '[data-tip-hover]', (e) => {
                const el = e.currentTarget;
                clearTimeout(this._hoverT);
                // 살짝 늦게 닫는다 — 아이콘 경계를 스치듯 지날 때 깜빡이지 않게
                this._hoverT = setTimeout(() => { if (this.anchor === el) this.hide(); }, 120);
            })
            // 바깥 클릭으로 닫기
            .on('click.jbtip', (e) => {
                if (!this.anchor) return;
                if (e.target === this.anchor || this.anchor.contains(e.target)) return;
                if (this.$panel && this.$panel[0].contains(e.target)) return;
                this.hide();
            })
            .on('keydown.jbtip', (e) => { if (e.key === 'Escape') this.hide(); });

        // 스크롤/리사이즈 시 따라다니기(열려 있을 때만)
        $(window).off('.jbtip').on('scroll.jbtip resize.jbtip', () => {
            if (this.anchor) this._place(this.anchor, this._lastPlace || 'bottom');
        });
    }

    _contentOf(el) {
        const ref = el.getAttribute('data-tip-html');
        if (ref) {
            if (ref[0] === '#') {
                const src = document.querySelector(ref);
                return src ? src.innerHTML : '';
            }
            return ref; // raw HTML
        }
        return this._esc(el.getAttribute('data-tip') || '');
    }

    _getPanel() {
        this._injectStyle();
        if (!this.$panel) {
            this.$panel = $(
                '<div class="jb-tip"><div class="jb-tip-arrow"></div><div class="jb-tip-body"></div></div>'
            ).appendTo('body');
        }
        return this.$panel;
    }

    /**
     * 기준 요소에 맞춰 팝오버 위치 계산(뷰포트 클램프 + 위/아래 자동 뒤집기 + 화살표).
     *
     * ⚠ **두 좌표계가 섞인다.** 관리자 글자크기는 `body { zoom }` 으로 만드는데, 팝오버도
     *   body 안에 있어 함께 확대된다. 그래서 좌표 단위가 갈린다:
     *     getBoundingClientRect() · innerWidth/Height → 확대된 화면 px
     *     outerWidth/Height() · 우리가 넣는 top/left  → 확대 전 로컬 px
     *   섞어 쓰면 '아주 크게' 에서 팝오버가 화면 밖으로 나간다 — 실제로 기사편집창 하단
     *   버튼들의 도움말이 뷰포트 아래로 200px 넘게 밀려 아예 안 보였다(zoom 1.3).
     *   계산은 화면 px 로 하고 **넣을 때만 zoom 으로 나눈다.**
     */
    _place(el, place) {
        this._lastPlace = place;
        const $p = this._getPanel();
        const z = this._zoom();
        const r = el.getBoundingClientRect();
        const pw = $p.outerWidth() * z;
        const ph = $p.outerHeight() * z;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        const gap = 10;
        const pad = 8;

        // 위/아래 결정(요청 방향 우선, 공간 없으면 반대로)
        let below = place !== 'top';
        if (below && r.bottom + gap + ph > vh - pad && r.top - gap - ph > pad) below = false;
        if (!below && r.top - gap - ph < pad && r.bottom + gap + ph < vh - pad) below = true;

        let top = below ? r.bottom + gap : r.top - gap - ph;

        // 좌우: 기준 요소 중앙에 정렬 후 뷰포트 안으로 클램프
        const cx = r.left + r.width / 2;
        let left = cx - pw / 2;
        left = Math.max(pad, Math.min(left, vw - pw - pad));

        $p.css({ top: Math.round(top / z), left: Math.round(left / z) });

        // 화살표 — 기준 중앙을 가리키도록 패널 기준 x 좌표 보정
        const ax = Math.max(12, Math.min(cx - left, pw - 12));
        $p.toggleClass('is-top', !below) // 패널이 기준 위에 있으면(화살표 아래로)
          .find('.jb-tip-arrow').css('left', Math.round(ax / z) - 6);
    }

    /**
     * 팝오버가 놓인 좌표계의 배율 — 관리자 글자크기(`body { zoom }`).
     * 팝오버는 body 에 붙으므로 body 의 zoom 이 곧 우리 배율이다.
     */
    _zoom() {
        const z = parseFloat(getComputedStyle(document.body).zoom);
        return z > 0 ? z : 1;
    }

    _injectStyle() {
        if (this._styleInjected) return;
        this._styleInjected = true;
        // 색·모양은 **관리자 테마 토큰**을 탄다. 이 팝오버가 쓰는 크림+앰버 대역은
        // 이미 `--adm-warn-bg` / `--adm-warn-border` 로 있다 (라이트값이 곧 현행 #fffbeb).
        // 폴백을 남기는 이유: 이 컴포넌트는 모바일(`/m/`)과 **공유**라 거기엔 --adm-* 가 없다.
        // 폴백값 = 현행 리터럴이므로 모바일 모습은 그대로다.
        const css = `
.jb-tip{position:fixed;z-index:var(--z-index-tooltip,1070);max-width:360px;
  background:var(--adm-warn-bg,#fffbeb);color:var(--adm-text,#374151);
  border:1px solid var(--adm-warn-border,#fde68a);border-radius:var(--adm-radius-lg,10px);
  padding:13px 15px;font-size:13px;line-height:1.65;
  box-shadow:var(--adm-shadow-dropdown,0 8px 28px rgba(0,0,0,.16));display:none}
.jb-tip.is-open{display:block}
.jb-tip.is-hover{pointer-events:none}
/* 팝오버 위에서 모달을 열 때 — 툴팁(1070)이 모달(1055)보다 위라 그냥 두면 창을 덮는다.
   닫지 않고 백드롭 아래로 물린다: 창을 닫으면 고르던 자리가 그대로 남아 있다 */
.jb-tip.is-under{z-index:1045}
.jb-tip .jb-tip-title{font-weight:700;font-size:13.5px;color:var(--adm-text-heading,#111827);
  margin:0 0 8px;padding-bottom:7px;border-bottom:1px solid var(--adm-warn-border,#fde68a)}
.jb-tip-arrow{position:absolute;top:-7px;width:12px;height:12px;background:var(--adm-warn-bg,#fffbeb);
  border-left:1px solid var(--adm-warn-border,#fde68a);border-top:1px solid var(--adm-warn-border,#fde68a);
  transform:rotate(45deg)}
.jb-tip.is-top .jb-tip-arrow{top:auto;bottom:-7px;transform:rotate(225deg)}
/* 패널톤 — 메모지가 아니라 표·카드와 같은 판. 안내문이 아니라 누를 것이 들어 있을 때 쓴다.
   바탕이 표와 같은 흰색이라 테두리·그림자가 껍데기를 대신 그린다 — 기본 테두리(--adm-border)와
   드롭다운 그림자로는 흰 표 위에서 판이 떠 보이지 않아 한 단계씩 올린다. */
.jb-tip--panel{background:var(--adm-surface,#fff);border-color:var(--adm-border-strong,#cbd5e1);
  box-shadow:var(--adm-shadow-modal,0 8px 16px rgba(0,0,0,.5))}
/* 안쪽 구분선은 테두리보다 옅게 — 같이 진해지면 칸막이가 판보다 세진다 */
.jb-tip--panel .jb-tip-title{border-bottom-color:var(--adm-border,#e5e7eb)}
/* 판톤 안의 '고르는 목록' — 회원 액션 팝오버와 처리 메뉴가 같은 모양이라 여기로 올린다.
   다음에 팝오버 메뉴가 필요하면 이 마크업만 내면 된다:
     <div class="jb-menu">
       <div class="jb-menu__h">무엇에 대한 메뉴인지</div>
       <div class="jb-menu__i" data-...><span><i class="fas fa-x jb-menu__ic"></i>라벨</span><em>부가</em></div>
       <hr>
       <div class="jb-menu__i is-danger">…</div>
     </div>
   위로 뒤집힌 팝오버(.is-top)는 앵커가 판 아래라 머리줄도 아래로 내린다. */
.jb-tip--panel .jb-menu{display:flex;flex-direction:column;min-width:210px;margin:-4px -2px}
.jb-tip--panel .jb-menu__h{font-size:12px;color:var(--adm-text-muted,#6b7280);line-height:1.4;
  padding:2px 6px 8px;margin-bottom:4px;border-bottom:1px solid var(--adm-border,#e5e7eb);
  max-width:260px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
/* 위로 뒤집힌 팝오버 — 앵커가 판 아래이므로 머리줄도 아래로 내린다.
   컨테이너를 뒤집지 않고 **머리줄만 order 로 옮긴다** — 통째로 뒤집으면 머리줄이 없는
   메뉴(회원 액션 팝오버처럼 목록만 든 것)에서 항목 순서까지 거꾸로 선다. */
.jb-tip--panel.is-top .jb-menu__h{order:1;padding:8px 6px 2px;margin:4px 0 0;
  border-bottom:0;border-top:1px solid var(--adm-border,#e5e7eb)}
.jb-tip--panel .jb-menu hr{margin:6px 0;border:0;border-top:1px solid var(--adm-border,#e5e7eb)}
.jb-tip--panel .jb-menu__i{display:flex;align-items:center;justify-content:space-between;gap:12px;
  padding:7px 8px;border-radius:var(--adm-radius-sm,4px);cursor:pointer;font-size:13px;
  color:var(--adm-text-heading,#111827);line-height:1.3;white-space:nowrap}
.jb-tip--panel .jb-menu__i:hover{background:var(--adm-row-hover,#f3f4f6)}
.jb-tip--panel .jb-menu__i em{font-style:normal;font-size:11.5px;color:var(--adm-text-muted,#6b7280)}
/* 라벨 앞 아이콘 — 폭을 고정한다. 아이콘마다 너비가 달라서 안 고정하면 글자 시작점이 줄마다 어긋난다 */
.jb-tip--panel .jb-menu__ic{display:inline-block;width:15px;margin-right:9px;text-align:center;
  font-size:11px;color:var(--adm-text-faint,#9ca3af)}
.jb-tip--panel .jb-menu__i:hover .jb-menu__ic{color:inherit}
.jb-tip--panel .jb-menu__i.is-danger span{color:var(--adm-ink-red,#b91c1c)}
/* 못 누르는 줄 — 없애지 않고 흐리게만 둔다. 없애면 '왜 없지' 를 묻게 된다 */
.jb-tip--panel .jb-menu__i.is-off{cursor:default;color:var(--adm-text-faint,#9ca3af)}
.jb-tip--panel .jb-menu__i.is-off:hover{background:none}
.jb-tip--panel .jb-tip-arrow{background:var(--adm-surface,#fff);
  border-left-color:var(--adm-border-strong,#cbd5e1);border-top-color:var(--adm-border-strong,#cbd5e1)}
`;
        $('<style>').text(css).appendTo('head');
    }

    _esc(s) {
        return String(s == null ? '' : s)
            .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
}
